import * as React from "react";
import { ActivityIndicator, Text, View } from "react-native";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as z from "zod";

import Logo from "~/assets/logo.svg";
import { colors } from "~/colors";
import { useHonoClient } from "~/hooks/HonoContext";
import { displayNameToSlug, FieldSync } from "~/hooks/useFieldSync";
import {
  createAccountSlugValidator,
  displayName,
  password,
  slug as slugSchema,
} from "~/lib/validation";
import { Button } from "~/ui/Button";
import { Form } from "~/ui/Form";
import { TextInput } from "~/ui/TextInput";

export function AccountForm(props: {
  email: string;
  onSubmit: (data: {
    password: string;
    displayName: string;
    slug: string;
  }) => Promise<any>;
}) {
  const { email, onSubmit } = props;
  const { hc } = useHonoClient();
  const schema = React.useMemo(() => {
    const slugValidator = createAccountSlugValidator({ apiClient: hc });
    return z.object({
      password,
      displayName,
      slug: slugSchema.refine(slugValidator, {
        message: "Username is not available",
      }),
    });
  }, []);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      displayName: "",
      slug: "",
      password: "",
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = form;
  return (
    <View className="container my-6 max-w-96 gap-8">
      <View className="items-center space-y-6">
        <Logo className="w-12" />
        <Text className="text-text-primary text-28 font-semibold">
          Create your account
        </Text>
      </View>
      <FormProvider {...form}>
        <Form>
          <View className="bg-grey-dark-0 flex-grow px-4">
            <View className="gap-4">
              <TextInput
                label="Invitation Email"
                inputMode="email"
                hint="You can change your main email later."
                value={email}
                editable={false}
              />
              <Controller
                control={control}
                name="password"
                render={({ field, fieldState: { error } }) => (
                  <TextInput
                    label="Set your password"
                    placeholder="Password123"
                    autoFocus
                    onChangeText={field.onChange}
                    value={field.value}
                    secureTextEntry
                    autoCapitalize="none"
                    autoComplete="off"
                    inputMode="text"
                    textContentType="newPassword"
                    passwordRules="minlength: 8"
                    errorMessage={error?.message}
                    autoCorrect={false}
                  />
                )}
              />
              <Controller
                control={control}
                name="displayName"
                render={({ field, fieldState: { error } }) => (
                  <TextInput
                    label="Display name"
                    hint="This is how your team mates will see you."
                    placeholder="John Doe"
                    onChangeText={field.onChange}
                    value={field.value ?? ""}
                    errorMessage={error?.message}
                    autoCorrect={false}
                  />
                )}
              />

              <Controller
                control={control}
                name="slug"
                render={({
                  field,
                  fieldState: { error, isValidating, isDirty },
                }) => (
                  <View className="relative">
                    <TextInput
                      label="Username"
                      hint="This is how you will be identified in URLs."
                      placeholder="johndoe1234"
                      onChangeText={(text) => {
                        setValue("slug", text, {
                          shouldValidate: true,
                        });
                      }}
                      value={field.value ?? ""}
                      autoCapitalize="none"
                      errorMessage={error?.message}
                      autoComplete="off"
                      data-1p-ignore
                    />
                    {isValidating && (
                      <ActivityIndicator
                        animating={isValidating}
                        size="small"
                        color={colors.text.primary}
                      />
                    )}
                  </View>
                )}
              />
              <FieldSync
                source="displayName"
                destination="slug"
                transformFn={displayNameToSlug}
              />

              <Button
                variant="primary"
                onPress={handleSubmit(onSubmit)}
                disabled={isSubmitting}
                role="button"
              >
                Create account
              </Button>
            </View>
          </View>
        </Form>
      </FormProvider>
    </View>
  );
}
